import { ReactElement, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { outreach, outreachQueries } from '@round/api';
import { Button, lucideIcons, Separator, toast, Tooltip } from '@round/creatorbase-ui';
import { BulkAction, useOutreachResultsBulkActions } from '../../hooks/useOutreachResultsBulkActions';
import { AddResultsToCampaignModal } from '../AddResultsToCampaignModal/AddResultsToCampaignModal';
import { BulkTagsPopover } from './components/BulkTagsPopover/BulkTagsPopover';
import { needsTagsToAdd, OutreachResultRow } from '../../helpers';
import styles from './OutreachResultsBulkActions.module.css';

const { Archive, ArchiveRestore, Megaphone, Plus, TriangleAlert } = lucideIcons;

const ACTIONS: BulkAction[] = ['add', 'archive', 'unarchive'];

/** Statuses each action accepts, which decides which buttons a selection gets. */
const ALLOWED_FROM: Record<BulkAction, outreach.OutreachResultStatus[]> = {
    // Adding from ADDED is a no-op on the backend, so those rows are excluded here too.
    add: ['TO_REVIEW'],
    archive: ['TO_REVIEW'],
    unarchive: ['ARCHIVED'],
};

/** ADDED rows still qualify since adding just puts them in the client's base; ARCHIVED ones don't. */
const ADD_TO_CAMPAIGN_ALLOWED_FROM: outreach.OutreachResultStatus[] = ['TO_REVIEW', 'ADDED'];

const ICONS: Record<BulkAction, typeof Plus> = {
    add: Plus,
    archive: Archive,
    unarchive: ArchiveRestore,
};

const LABELS: Record<BulkAction, string> = {
    add: 'Add',
    archive: 'Archive',
    unarchive: 'Unarchive',
};

const DONE_LABELS: Record<BulkAction, string> = {
    add: 'Added',
    archive: 'Archived',
    unarchive: 'Unarchived',
};

const CLEAR_LABEL = 'Clear';

const ADD_TO_CAMPAIGN_LABEL = 'Add to campaign';

const ADD_TAGS_LABEL = 'Add tags';

/** Tags can only be edited while a result is in review. */
const ADD_TAGS_ALLOWED_FROM: outreach.OutreachResultStatus[] = ['TO_REVIEW'];

const GROUP_LABEL = 'Actions for the selected creators';

const pluralizeCreators = (count: number) => `${count} ${count === 1 ? 'creator' : 'creators'}`;

/** A selection can hold rows an action is refused for, so the label counts the ones it covers. */
const getActionLabel = (label: string, eligibleCount: number, selectedCount: number) =>
    eligibleCount === selectedCount ? label : `${label} ${eligibleCount}`;

const getUntaggedHint = (untaggedCount: number, eligibleCount: number) => {
    if (!eligibleCount) {
        return untaggedCount === 1 ? 'Tag this creator before adding them' : 'Tag these creators before adding them';
    }

    return `${pluralizeCreators(untaggedCount)} without tags won't be added`;
};

type Props = {
    outreachRequestId: number;
    /** The loaded rows, where a selected id is read back as a status. */
    rows: OutreachResultRow[];
    selectedIds: Set<number>;
    onClearSelection: () => void;
    getAvatarUrl: (userId: number) => string | undefined;
    areAvatarsLoading: boolean;
};

export const OutreachResultsBulkActions = ({
    outreachRequestId,
    rows,
    selectedIds,
    onClearSelection,
    getAvatarUrl,
    areAvatarsLoading,
}: Props) => {
    const { runBulkAction, runningAction } = useOutreachResultsBulkActions();
    const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
    const isTagging = useIsMutating({ mutationKey: outreachQueries.outreachRequestResults.keys.bulkUpdate() }) > 0;
    const isBusy = !!runningAction || isTagging;

    if (!selectedIds.size) {
        return null;
    }

    const selectedRows = rows.filter((row) => selectedIds.has(row.id));
    const allowedCampaignRows = selectedRows.filter((row) => ADD_TO_CAMPAIGN_ALLOWED_FROM.includes(row.status));
    // Adding to a campaign adds the creator first, so untagged rows in review sit it out too.
    const campaignUntaggedCount = allowedCampaignRows.filter(needsTagsToAdd).length;
    const addToCampaignRows = allowedCampaignRows.filter((row) => !needsTagsToAdd(row));
    const addTagsIds = selectedRows.filter((row) => ADD_TAGS_ALLOWED_FROM.includes(row.status)).map((row) => row.id);

    const handleAction = async (action: BulkAction, ids: number[]) => {
        const { succeededCount, failedCount, errorMessage } = await runBulkAction(action, ids);

        if (succeededCount) {
            toast.success(`${DONE_LABELS[action]} ${pluralizeCreators(succeededCount)}`);
        }

        if (failedCount) {
            // A single failure gets its own message; multiple failures are just counted.
            toast.error(
                failedCount === 1 && errorMessage
                    ? errorMessage
                    : `Could not ${action} ${pluralizeCreators(failedCount)}`
            );
        }

        // Clear the selection so the same batch can't be re-run over rows it already changed.
        onClearSelection();
    };

    return (
        <div className={styles.container} role="group" aria-label={GROUP_LABEL}>
            <span className={styles.count}>{selectedIds.size} selected</span>

            <Button variant="ghost" size="xs" onClick={onClearSelection} disabled={isBusy}>
                {CLEAR_LABEL}
            </Button>

            {ACTIONS.map((action) => {
                const allowedRows = selectedRows.filter((row) => ALLOWED_FROM[action].includes(row.status));

                if (!allowedRows.length) {
                    return null;
                }

                // Adding is also refused for a result in review with no tags, so those rows sit the batch out.
                const untaggedCount = action === 'add' ? allowedRows.filter(needsTagsToAdd).length : 0;
                const eligibleIds = allowedRows
                    .filter((row) => action !== 'add' || !needsTagsToAdd(row))
                    .map((row) => row.id);

                const Icon = ICONS[action];

                return withUntaggedHint(
                    <Button
                        key={action}
                        variant="outline"
                        size="xs"
                        onClick={() => handleAction(action, eligibleIds)}
                        disabled={isBusy || !eligibleIds.length}
                        isLoading={runningAction === action}
                    >
                        <Icon className={styles.icon} aria-hidden="true" />
                        {eligibleIds.length
                            ? getActionLabel(LABELS[action], eligibleIds.length, selectedIds.size)
                            : LABELS[action]}
                        {untaggedCount > 0 && eligibleIds.length > 0 && (
                            <TriangleAlert className={styles.warningIcon} aria-hidden="true" />
                        )}
                    </Button>,
                    action,
                    untaggedCount,
                    eligibleIds.length
                );
            })}

            {allowedCampaignRows.length > 0 &&
                withUntaggedHint(
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => setIsCampaignModalOpen(true)}
                        disabled={isBusy || !addToCampaignRows.length}
                    >
                        <Megaphone className={styles.icon} aria-hidden="true" />
                        {addToCampaignRows.length
                            ? getActionLabel(ADD_TO_CAMPAIGN_LABEL, addToCampaignRows.length, selectedIds.size)
                            : ADD_TO_CAMPAIGN_LABEL}
                        {campaignUntaggedCount > 0 && addToCampaignRows.length > 0 && (
                            <TriangleAlert className={styles.warningIcon} aria-hidden="true" />
                        )}
                    </Button>,
                    ADD_TO_CAMPAIGN_LABEL,
                    campaignUntaggedCount,
                    addToCampaignRows.length
                )}

            {addTagsIds.length > 0 && (
                <BulkTagsPopover
                    outreachRequestId={outreachRequestId}
                    resultIds={addTagsIds}
                    label={getActionLabel(ADD_TAGS_LABEL, addTagsIds.length, selectedIds.size)}
                    disabled={isBusy}
                    onApplied={onClearSelection}
                />
            )}

            <Separator orientation="vertical" className={styles.separator} />

            {/* Mounted only while open, so each run starts on an empty campaign and empty costs. */}
            {isCampaignModalOpen && (
                <AddResultsToCampaignModal
                    results={addToCampaignRows}
                    getAvatarUrl={getAvatarUrl}
                    areAvatarsLoading={areAvatarsLoading}
                    onClose={() => setIsCampaignModalOpen(false)}
                    onAdded={onClearSelection}
                />
            )}
        </div>
    );
};

const withUntaggedHint = (button: ReactElement, key: string, untaggedCount: number, eligibleCount: number) => {
    if (!untaggedCount) {
        return button;
    }

    return (
        <Tooltip key={key}>
            {/* A disabled button swallows pointer events, so the trigger sits on a wrapper. */}
            <Tooltip.Trigger asChild>
                <span className={styles.tooltipTrigger}>{button}</span>
            </Tooltip.Trigger>
            <Tooltip.Content>{getUntaggedHint(untaggedCount, eligibleCount)}</Tooltip.Content>
        </Tooltip>
    );
};
